import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { AddAddressButton } from "@pages/AdminDashboard/AddressManager/AddAddressButton";
import { AddressFields } from "@pages/AdminDashboard/AddressManager/AddressFields";
import {
  emptyAddressFields,
  isAddressComplete,
  toAddressFields,
} from "@pages/AdminDashboard/AddressManager/addressFormValues";
import { describeShipAddress } from "@pages/AdminDashboard/ShipScreen/shipAddressFormat";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { Card } from "@pages/AdminDashboard/ui/Card";
import { VisuallyHidden } from "@pages/AdminDashboard/ui/DataTable/visuallyHidden";
import { Tag } from "@pages/AdminDashboard/ui/StatusBadge";
import { saveUserAddress } from "@store/admin/adminAction";
import { userAddressSaveCleared } from "@store/admin/adminSlice";
import { ShipAddressFields, UserAddressSaveState } from "@types-runway/admin";
import { RootState } from "@types-runway/state";
import { Address } from "@types-runway/user";

const { color, radius, typeScale } = adminTokens;
const [, spaceSm, spaceMd] = adminTokens.spacing;
// No save of this user's: the save on record is another user's, or the admin state was rehydrated
// from a session stored before this state existed.
const notSaving: UserAddressSaveState = { userId: null, status: "idle", failure: null };

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceSm}px;
`;

const AddressCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spaceMd}px;
  padding: ${spaceSm}px ${spaceMd}px;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  background-color: ${color.surface};
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Street = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.labelValue.fontSize};
`;

const Place = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const CardActions = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${spaceSm}px;
`;

const EmptyMessage = styled.p`
  margin: 0;
  color: ${color.inkSoft};
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceMd}px;
`;

const FormTitle = styled.h4`
  margin: 0;
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.labelValue.fontSize};
  font-weight: normal;
`;

// On a phone the Buttons stand one per row, "Save address" first (FIND-R23).
const FormActions = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: ${({ $isPhone }) => ($isPhone ? "column" : "row")};
  align-items: ${({ $isPhone }) => ($isPhone ? "stretch" : "normal")};
  justify-content: flex-end;
  gap: ${spaceSm}px;
`;

interface UserAddressesProps {
  userId: string;
  addresses: Address[];
}

type ManagerMode = "list" | "form";

// The found user's saved addresses, managed as on the Ship screen (USER-R14, R15, R22…R29; FIND-E5,
// FIND-E7): a card per address with Edit beside it, "+ Add a new address" below, and a form that
// takes their place until the address is saved.
const UserAddresses = ({ userId, addresses }: UserAddressesProps) => {
  const isPhone = useIsPhoneLayout();
  const dispatch = useDispatch();
  const save = useSelector<RootState, UserAddressSaveState | undefined>(
    (state) => state.admin.userAddressSave
  );
  const { status, failure } = save?.userId === userId ? save : notSaving;
  const [mode, setMode] = useState<ManagerMode>("list");
  const [fields, setFields] = useState<ShipAddressFields>(emptyAddressFields);

  const isSaving = status === "saving";

  // A saved address is listed, and the form gives way to the cards (USER-R27).
  useEffect(() => {
    if (status === "saved") setMode("list");
  }, [status]);

  const openForm = (address?: Address) => {
    dispatch(userAddressSaveCleared());
    setFields(address ? toAddressFields(address) : emptyAddressFields);
    setMode("form");
  };

  const saveForm = () => dispatch(saveUserAddress({ userId, fields }));

  if (mode === "form") {
    const backButton = (
      <Button key="back" look="secondary" disabled={isSaving} onClick={() => setMode("list")}>
        Back
      </Button>
    );
    const saveButton = (
      <Button
        key="save"
        look="commit"
        disabled={!isAddressComplete(fields)}
        isPending={isSaving}
        pendingLabel="Save address"
        onClick={saveForm}
      >
        Save address
      </Button>
    );

    return (
      <div data-testid="user-addresses">
        <Card title="Saved addresses" titleLevel={3}>
          <Form>
            <FormTitle>{fields.id ? "Edit address" : "Add a new address"}</FormTitle>
            {status === "failed" && (
              <Banner
                tone="error"
                title="The address wasn't saved."
                body={`Request failed (${failure}). Nothing changed.`}
              />
            )}
            <AddressFields fields={fields} onChange={setFields} />
            <FormActions $isPhone={isPhone}>
              {isPhone ? [saveButton, backButton] : [backButton, saveButton]}
            </FormActions>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="user-addresses">
      <Card title="Saved addresses" titleLevel={3}>
        <Rows>
          {addresses.length === 0 && <EmptyMessage>No addresses on file.</EmptyMessage>}
          {addresses.map((address) => (
            <AddressCard key={address.id} data-testid={`user-address-${address.id}`}>
              <Lines>
                <Street>{address.street}</Street>
                {address.secondary && <Place>{address.secondary}</Place>}
                <Place>{`${address.city}, ${address.state} ${address.postalCode}`}</Place>
              </Lines>
              <CardActions>
                {address.default && <Tag>Default</Tag>}
                <Button look="secondary" onClick={() => openForm(address)}>
                  Edit<VisuallyHidden> {describeShipAddress(address)}</VisuallyHidden>
                </Button>
              </CardActions>
            </AddressCard>
          ))}
          <AddAddressButton onClick={() => openForm()} />
        </Rows>
      </Card>
    </div>
  );
};

export default UserAddresses;
